import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isBefore, startOfDay } from "date-fns";
import { useSnackbar } from "notistack";
import { IRecall } from "../../../@types/recalls";

type Props = {
    open: boolean;
    recall: IRecall | null;
    onClose: VoidFunction;
    onSave: (data: Partial<IRecall>) => void;
};

const todayStart = () => startOfDay(new Date());

const isPastRecallDate = (date: Date | null): boolean =>
    !!date && isBefore(startOfDay(date), todayStart());

export default function EditRecallDialog({ open, recall, onClose, onSave }: Props) {
    const { enqueueSnackbar } = useSnackbar();
    const [recallType, setRecallType] = useState("");
    const [communicationType, setCommunicationType] = useState<"SMS" | "Email" | "Phone Call">("SMS");
    const [recallDate, setRecallDate] = useState<Date | null>(null);
    const [message, setMessage] = useState("");
    const [dateError, setDateError] = useState("");

    useEffect(() => {
        if (recall) {
            setRecallType(recall.recallType || "");
            setCommunicationType(recall.communicationType || "SMS");
            const parsed = recall.recallDate ? new Date(recall.recallDate) : null;
            setRecallDate(parsed);
            setDateError(isPastRecallDate(parsed) ? "Recall date cannot be in the past" : "");
            setMessage(recall.message || "");
        }
    }, [recall]);

    const handleRecallDateChange = (newValue: Date | null) => {
        if (isPastRecallDate(newValue)) {
            setDateError("Recall date cannot be in the past");
            setRecallDate(newValue);
            return;
        }
        setDateError("");
        setRecallDate(newValue);
    };

    const handleSave = () => {
        if (!recallDate) {
            enqueueSnackbar("Please select a recall date", { variant: "error" });
            return;
        }

        if (isPastRecallDate(recallDate)) {
            setDateError("Recall date cannot be in the past");
            enqueueSnackbar("Recall date cannot be in the past", { variant: "error" });
            return;
        }

        onSave({
            recallType,
            communicationType,
            scheduledDate: recallDate,
            message,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Recall</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <TextField
                        label="Recall Type"
                        value={recallType}
                        onChange={(e) => setRecallType(e.target.value)}
                        fullWidth
                    />

                    <TextField
                        select
                        label="Communication Type"
                        value={communicationType}
                        onChange={(e) => setCommunicationType(e.target.value as "SMS" | "Email" | "Phone Call")}
                        fullWidth
                    >
                        <MenuItem value="SMS">SMS</MenuItem>
                        <MenuItem value="Email">Email</MenuItem>
                        <MenuItem value="Phone Call">Phone Call</MenuItem>
                    </TextField>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Recall Date"
                            value={recallDate}
                            onChange={handleRecallDateChange}
                            minDate={todayStart()}
                            disablePast
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!dateError,
                                    helperText: dateError,
                                },
                            }}
                        />
                    </LocalizationProvider>

                    <TextField
                        label="Message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!!dateError}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
